import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { MapPin, Phone, Clock, Instagram } from 'lucide-react';

const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [isOpen, setIsOpen] = useState(true);
  const [currentTime, setCurrentTime] = useState('');

  useEffect(() => {
    const checkOpenStatus = () => {
      const now = new Date();
      const hours = now.getHours();
      // Open from 08:00 to 00:00 (midnight)
      const open = hours >= 8 || hours === 0;
      setIsOpen(open);

      // Format time for Turkey
      setCurrentTime(now.toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Istanbul'
      }));
    };

    checkOpenStatus();
    const interval = setInterval(checkOpenStatus, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  return (
    <section
      id="contact"
      ref={ref}
      className="py-24 md:py-32 bg-[#2d4a3e]"
      data-testid="contact-section"
    >
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 md:mb-24"
        >
          <span className="font-accent text-[#d4a84b] text-2xl" data-testid="contact-label">
            Ziyaret edin
          </span>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-bold text-[#f5f0e1] mt-4" data-testid="contact-title">
            İletişim
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            {/* Status indicator */}
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-[#1a2e23] rounded-sm" data-testid="status-indicator">
              <span className={`w-3 h-3 rounded-full ${isOpen ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
              <span className={`font-semibold ${isOpen ? 'text-green-500' : 'text-red-500'}`}>
                {isOpen ? 'Şu an Açık' : 'Şu an Kapalı'}
              </span>
              <span className="text-[#b8c4b8]">• 00:00'da kapanıyor</span>
            </div>

            {/* Contact cards */}
            <div className="space-y-4">
              {/* Address */}
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=39.8921071,32.8468955"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 p-6 bg-[#1a2e23] rounded-sm hover:bg-[#3d5a4e] transition-colors group"
                data-testid="address-card"
              >
                <div className="p-3 bg-[#3d5a4e] rounded-sm group-hover:bg-[#d4a84b]/20 transition-colors">
                  <MapPin className="w-6 h-6 text-[#d4a84b]" />
                </div>
                <div>
                  <p className="text-[#f5f0e1] font-semibold mb-1">Adres</p>
                  <p className="text-[#b8c4b8]">
                    Ayrancı, Hoşdere Cd. No:116 D:C<br />
                    06540 Çankaya / Ankara / Türkiye
                  </p>
                </div>
              </a>

              {/* Phone */}
              <a
                href="tel:+905324846373"
                className="flex items-start gap-4 p-6 bg-[#1a2e23] rounded-sm hover:bg-[#3d5a4e] transition-colors group"
                data-testid="phone-card"
              >
                <div className="p-3 bg-[#3d5a4e] rounded-sm group-hover:bg-[#d4a84b]/20 transition-colors">
                  <Phone className="w-6 h-6 text-[#d4a84b]" />
                </div>
                <div>
                  <p className="text-[#f5f0e1] font-semibold mb-1">Telefon</p>
                  <p className="text-[#b8c4b8]">0532 484 63 73</p>
                  <p className="text-[#d4a84b] text-sm mt-1">Tıklayın ve arayın</p>
                </div>
              </a>

              {/* Instagram */}
              <a
                href="https://www.instagram.com/poljecoffeeco/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 p-6 bg-[#1a2e23] rounded-sm hover:bg-[#3d5a4e] transition-colors group"
                data-testid="instagram-card"
              >
                <div className="p-3 bg-[#3d5a4e] rounded-sm group-hover:bg-[#d4a84b]/20 transition-colors">
                  <Instagram className="w-6 h-6 text-[#d4a84b]" />
                </div>
                <div>
                  <p className="text-[#f5f0e1] font-semibold mb-1">Instagram</p>
                  <p className="text-[#b8c4b8]">@poljecoffeeco</p>
                  <p className="text-[#d4a84b] text-sm mt-1">Takip edin</p>
                </div>
              </a>

              {/* Hours */}
              <div className="flex items-start gap-4 p-6 bg-[#1a2e23] rounded-sm" data-testid="hours-card">
                <div className="p-3 bg-[#3d5a4e] rounded-sm">
                  <Clock className="w-6 h-6 text-[#d4a84b]" />
                </div>
                <div>
                  <p className="text-[#f5f0e1] font-semibold mb-1">Çalışma Saatleri</p>
                  <p className="text-[#b8c4b8]">Her gün 08:00 - 00:00</p>
                  <p className="text-[#6b7b6b] text-sm mt-1">Şu anki saat: {currentTime}</p>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=39.8921071,32.8468955"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 border border-[#d4a84b] text-[#d4a84b] hover:bg-[#d4a84b] hover:text-[#1a2e23] transition-all duration-300 rounded-sm font-medium"
              data-testid="directions-button"
            >
              <MapPin className="w-5 h-5" />
              Yol Tarifi Al
            </a>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-sm h-[400px] lg:h-full min-h-[500px]" data-testid="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1530.3429304192135!2d32.8468955!3d39.8921071!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDUzJzMxLjYiTiAzMsKwNTAnNDguOCJF!5e0!3m2!1str!2str!4v1709294800000"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(83%)' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Polje Coffee Co Location"
                className="absolute inset-0"
              />
              {/* Map overlay info */}
              <div className="absolute bottom-6 left-6 right-6 glass p-4 rounded-sm">
                <p className="text-[#f5f0e1] font-['Playfair_Display'] text-lg font-semibold">Polje Coffee Co</p>
                <p className="text-[#b8c4b8] text-sm">Çankaya, Ankara</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
